import axiosInstance from './axiosInstance.js'

// Backend errors are `{ detail: { error } }`; falls back to a generic "<label> (<status>)" message,
// or just "<label>" when there's no response at all (network/CORS failure, no status to report).
function extractApiErrorMessage(err, fallback) {
  const msg = err.response?.data?.detail?.error || err.response?.data?.message || (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null)
  if (msg) return msg
  const status = err.response?.status
  return status != null ? `${fallback} (${status})` : fallback
}

export const AGENT_POLL_TIMEOUT_MS = 20 * 60 * 1000  // classify with pre-clean, or an architect review

export async function pollJobStatus(jobId, { intervalMs = 1500, timeoutMs = 5 * 60 * 1000, label = 'Processing', onProgress, statusUrl } = {}) {
  const defaultUrl = `/status/${encodeURIComponent(jobId)}`
  const reviewUrl = `/agent/status/${encodeURIComponent(jobId)}`
  if (statusUrl && statusUrl !== reviewUrl) throw new Error('Invalid job status URL')
  const pollUrl = statusUrl || defaultUrl
  const deadline = Date.now() + timeoutMs
  while (true) {
    await new Promise(r => setTimeout(r, intervalMs))
    let s
    try {
      const res = await axiosInstance.get(pollUrl)
      s = res.data
    } catch (err) {
      throw new Error(extractApiErrorMessage(err, `${label} failed`))
    }
    if (s.status === 'done')  return s
    if (s.status === 'stopped' || s.status === 'timeout') {
      const error = new Error(s.detail?.error || (s.status === 'stopped' ? 'Job stopped' : 'Job timed out'))
      error.code = s.detail?.code || s.status.toUpperCase()
      throw error
    }
    if (s.status === 'error') throw new Error(s.detail?.error || `${label} failed`)
    if (s.meta) onProgress?.(s.meta)
    if (Date.now() > deadline) {
      const e = new Error(`${label} timed out  —  the file may be too large or the server is busy.`)
      e.timedOut = true
      throw e
    }
  }
}

export async function uploadDxf(form) {
  try {
    const res = await axiosInstance.post('/upload', form)
    return res.data
  } catch (err) {
    const detail = err.response?.data?.detail
    throw new Error(detail?.error || detail || `Upload failed (${err.response?.status})`)
  }
}

// The floor and core of the classified plan (poll the job: its result is {floor, core, stats} in drawing units). `edits` are the user's
// current edits, so the picture the floor model gets is the plan as the user sees it.
export async function detectFloor(sessionId, edits, { signal, isCurrent = () => true, intervalMs = 1500, timeoutMs = AGENT_POLL_TIMEOUT_MS } = {}) {
  const deadline = Date.now() + timeoutMs
  while (true) {
    if (signal?.aborted || !isCurrent()) throw new Error('Floor detection cancelled: drawing changed or stopped.')
    try {
      return (await axiosInstance.post('/detect_floor', { session_id: sessionId, edits }, { signal })).data
    } catch (err) {
      if (err.response?.status !== 409 || err.response?.data?.code !== 'NOT_FINISHED') {
        throw new Error(extractApiErrorMessage(err, 'Floor detection failed'))
      }
      if (Date.now() >= deadline) throw new Error('Classification is still running. Try floor detection after it finishes.')
      await new Promise(resolve => setTimeout(resolve, intervalMs))
    }
  }
}

export async function classify(sessionId, { cmPerUnit } = {}) {
  try {
    const res = await axiosInstance.post('/classify', {
      session_id: sessionId, cm_per_unit: cmPerUnit ?? null,
    })
    return res.data
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Failed'))
  }
}

// The user's answer to one of the agent's questions (there is no free-text chat).
export async function agentChat(sessionId, { message, edits, answer, client_job_ref } = {}) {
  try {
    const res = await axiosInstance.post('/agent_chat', { session_id: sessionId, message, edits: edits ?? {}, answer: answer ?? null, client_job_ref })
    return res.data
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Could not send the answer'))
  }
}

// What happened to changes the agent applied: { op_id: 'applied' | 'undone' }, for the training log.
export async function agentFeedback(sessionId, decisions) {
  if (!Object.keys(decisions).length) return
  await axiosInstance.post('/agent_feedback', { session_id: sessionId, decisions })
}

// Stop the running classify-agent job at its next step; its poll then returns what it staged.
export async function agentStop(sessionId) {
  try {
    await axiosInstance.post('/agent_stop', { session_id: sessionId })
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Could not stop classify-agent'))
  }
}

// What the UI may show this user ({ staff }); the server enforces the same rules.
export async function getMe() {
  try {
    const res = await axiosInstance.get('/me')
    return res.data
  } catch {
    return { staff: false }
  }
}

export async function agentClassify(sessionId) {
  try {
    const res = await axiosInstance.post('/agent_classify', { session_id: sessionId })
    return res.data
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Could not start the review'))
  }
}

export async function downloadAudit(sessionId) {
  try {
    const res = await axiosInstance.get('/download_audit', {
      params: { session_id: sessionId },
      responseType: 'blob',
    })
    return res.data
  } catch {
    return null
  }
}

export function saveFeedback(payload) {
  return axiosInstance.post('/save_feedback', payload)
}

export async function getSessions(signal) {
  try {
    const res = await axiosInstance.get('/sessions', { signal })
    return Array.isArray(res.data) ? res.data : []
  } catch (err) {
    if (err.code === 'ERR_CANCELED') throw err
    throw new Error(extractApiErrorMessage(err, 'Failed to load sessions'))
  }
}

export async function getSessionStats() {
  try {
    const res = await axiosInstance.get('/sessions/stats')
    return res.data
  } catch {
    return null
  }
}

export async function reopenSession(sessionId) {
  try {
    const res = await axiosInstance.get(`/sessions/${sessionId}/reopen`)
    return res.data
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Failed to reopen session'))
  }
}

export async function saveCanvasState(sessionId, state, expectedRevision) {
  try {
    const res = await axiosInstance.put(`/sessions/${sessionId}/state`, { state, expected_revision: expectedRevision })
    return res.data
  } catch (err) {
    const e = new Error(extractApiErrorMessage(err, 'Autosave failed'))
    e.status = err.response?.status
    e.currentRevision = err.response?.data?.detail?.current_revision
    throw e
  }
}

export async function rerunLayerClassifier(sessionId, classAssignments) {
  try {
    const res = await axiosInstance.post('/rerun_layer_classifier', {
      session_id: sessionId,
      class_assignments: classAssignments,
    })
    return res.data
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Revalidation failed'))
  }
}


export async function resolveFloor(sessionId, jobId, answers, reviewToken, revisionId) {
  try {
    return (await axiosInstance.post('/floor/resolve', { session_id: sessionId, job_id: jobId, answers, review_token: reviewToken, revision_id: revisionId })).data
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Could not apply room decisions'))
  }
}

export async function previewFloorEdit(sessionId, edit) {
  try {
    return (await axiosInstance.post('/floor/preview', { session_id: sessionId, edit })).data
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Could not update floor and core'))
  }
}
